import { AppButton } from '@clickguard/design-system'
import { useState, useCallback, ChangeEvent } from 'react'
import { GDialog, GInput } from 'components/basic-blocks'
import { useStores } from 'hooks'
import { Assets } from 'services/user/assets'
import { toast } from 'react-toastify'

export const EmailDeveloper = ({
	onDirectComplete
}: {
	onDirectComplete?: () => Promise<void>
}) => {
	const {
		assetStore: { asset },
		accountStore: { account }
	} = useStores()

	const [email, setEmail] = useState('')
	const [isSending, setIsSending] = useState(false)
	const [showConfirmation, setShowConfirmation] = useState(false)

	const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value)
	}, [])

	const handleSend = useCallback(async () => {
		if (!email.trim()) return
		if (!account?.id || !asset?.id) {
			toast.error('Account or asset information is missing')
			return
		}

		setIsSending(true)
		try {
			await Assets.sendTrackingCodeEmail(account.id, asset.id, email)
			setEmail('')
			setShowConfirmation(true)
		} catch (error: unknown) {
			const err = error as { response?: { data?: { message?: string } } }
			toast.error(
				err?.response?.data?.message ||
					'Failed to send email. Please try again.'
			)
			console.error('Error sending tracking code email:', error)
		} finally {
			setIsSending(false)
		}
	}, [email, account?.id, asset?.id])

	return (
		<div className="flex flex-col gap-5">
			{/* Confirmation Modal */}
			<GDialog
				open={showConfirmation}
				onClose={() => setShowConfirmation(false)}
				showClose={false}
				maxWidth="xl"
				className="px-[48px] py-[32px]"
			>
				<div className="flex flex-col gap-[16px] items-start">
					<div className="flex flex-col gap-[16px] items-start w-full">
						<h2 className="text-[30px] leading-[36px] font-medium text-t-email-blue text-center w-full">
							Instructions sent!
						</h2>
						<p className="text-[16px] leading-[22px] text-t-google-tooltip text-center w-full">
							Once your developer completes the tracking code <br />
							installation, your setup will be complete. We’ll keep checking in{' '}
							<br />
							the background and update your status automatically once it’s
							confirmed.
							<br />
							<br />
							In the meantime, you may explore ClickGuard’s dashboard.
						</p>
					</div>
					<div className="flex items-center justify-center pt-[8px] w-full">
						<AppButton
							variant="marketing"
							size="md"
							label="Go to dashboard"
							onClick={onDirectComplete}
						/>
					</div>
				</div>
			</GDialog>

			<div className="flex gap-6 items-end">
				<div className="flex-1">
					<GInput
						name="developer-email"
						placeholder="Fill in a valid email"
						type="email"
						value={email}
						onChange={handleEmailChange}
						inputClassName="w-full text-[16px] leading-[22px] text-t-coupon placeholder:text-t-gray-hold"
					/>
				</div>
				<div className="py-[2px]"></div>
			</div>
			<AppButton
				variant="marketing"
				size="lg"
				label="Send instructions"
				isLoading={isSending}
				loader="dots"
				disabled={!email.trim() || isSending}
				onClick={handleSend}
			/>
		</div>
	)
}
